/* eslint-disable no-useless-catch */
import { DeviceType, JwtPayload } from '@/utils/DataTypes'
import { useAppStore } from '@/stores/appStore'
import i18n from '@/translations/i18n'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const api = async (url: string, method: string, data?: any): Promise<any | null> => {
  const appStore = useAppStore()
  const accessToken = appStore.getAppState.accessToken || null

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const request: any = {
    method: method,
    headers: {
      ...{ Authorization: `Bearer ${accessToken}` }
    }
  }

  if (data != null) {
    request.headers['Content-Type'] = 'application/json'
    request.body = JSON.stringify(data)
  }

  const fullUrl = '/api/' + url.replace(/^\//, '')
  const response = await fetch(fullUrl, request)

  if (response.ok) {
    const parsedResponse = await response.json()
    const responseData = parsedResponse.data
    return {
      data: responseData,
      status: response.status,
      statusText: response.statusText,
      headers: {},
      config: {}
    }
  } else {
    throw new Error(`API request ${url} failed with status ${response.status}: ${response.statusText}`)
  }
}

export const findDeviceTypeById = (deviceTypeList: DeviceType[], id: number): string | null => {
  const itemType = deviceTypeList.find(item => item.id === id)
  return itemType ? itemType.name : null
}

export const decodeToken = (inputToken = null): JwtPayload | null => {
  const authToken = inputToken || localStorage.getItem('accessToken')

  if (!authToken) return null

  try {
    const base64Url = authToken.split('.')[1] // Extract the payload part
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/') // Base64 format adjustment for URL-safe encoding
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    }).join(''))

    return JSON.parse(jsonPayload) as JwtPayload
  } catch (error) {
    console.error('Failed to decode token:', error)
    return null
  }
}

export const getRandomUid = () => {
  return Math.random().toString(36).substring(2, 10)
}

export const getDateTime = (submissionDate: string): string => {
  const todayTextTranslation = i18n.global.t('i18n.today')
  const atTextTranslation = i18n.global.t('i18n.at')
  const date = new Date(submissionDate)

  if (isToday(date)) {
    return `${todayTextTranslation.charAt(0).toUpperCase() + todayTextTranslation.slice(1)} ${atTextTranslation} ${date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
      // second: '2-digit'
    })}`
  } else {
    return date.toLocaleDateString(i18n.global.locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }) + ` ${atTextTranslation} ${date.toLocaleTimeString(i18n.global.locale, {
      hour: '2-digit',
      minute: '2-digit'
      // second: '2-digit'
    })}`
  }
}

export const isToday = (date: Date): boolean => {
  const today = new Date()
  return date.toDateString() === today.toDateString()
}

export const downloadCSV = (headers: (string | number)[][], rows: (string | number)[][], filename = 'data.csv') => {
  try {
    if (headers.length === 0 || rows.length === 0) return

    const headerRows = headers.map(hrow => hrow.join(';')).join('\n')
    const dataRows = rows.map(row => row.join(';')).join('\n')

    const csvContent = '\ufeff' + headerRows + '\n' + dataRows

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error: unknown) {
    let errorMessage = 'An unknown error occurred.'

    if (error instanceof Error) {
      errorMessage = error.message
    } else if (typeof error === 'string') {
      errorMessage = error
    }

    console.error('Error exporting CSV:', errorMessage)
    throw error // Fazer handling / exibir mensagem no frontend
  }
}

export const hasPermissions = (permissionName: string): boolean => {
  const appStore = useAppStore()
  const userPermissions = appStore.$state.permissions || []

  return userPermissions.includes(permissionName)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).timers = {}
// eslint-disable-next-line @typescript-eslint/ban-types
function startTimer (timerName: string, f: Function, delay: number, activations?: number) {
  stopTimer(timerName)

  if (activations === 0) {
    console.log(`Timer ${timerName} not started because activations are zero.`)
    return
  }
  if (delay == null) {
    console.log(`Timer ${timerName} not started because delay is null or zero.`)
    return
  }
  if (typeof f !== 'function') {
    console.log(`Timer ${timerName} not started because f is not a function`)
    return
  }

  const callback = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const timer = (window as any).timers[timerName]
    if (timer == null) {
      return
    }
    try {
      f()
      timer.success++
    } catch (e) {
      timer.errors++
      console.error(e)
    }
    if (timer.remaining != null) {
      timer.remaining--
      if (timer.remaining === 0) {
        stopTimer(timerName)
        return
      }
    }
    timer.timeoutId = setTimeout(callback, timer.delay)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).timers[timerName] = {
    activations,
    remaining: activations,
    error: 0,
    success: 0,
    delay,
    timeoutId: setTimeout(callback, delay)
  }
  console.log(`Timer ${timerName} started`)
}

function stopTimer (timerName: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const timer = (window as any).timers[timerName]
  if (timer != null) {
    console.log(`Timer ${timerName} stopped`)
    clearTimeout(timer.timeoutId)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (window as any).timers[timerName]
  }
}

export const timers = {
  start: startTimer,
  stop: stopTimer
}
